import CategoryCard from '@components/CategoryCard/CategoryCard.component'
import TimeSelectionCard from '@components/TimeSelectionCard/TimeSelectionCard.component'
import {
  Box,
  Button,
  Card,
  Container,
  Fab,
  IconButton,
  InputAdornment,
  styled,
  TextField,
  Typography,
} from '@mui/material'
import { createFileRoute, useSearch, useNavigate, useLocation } from '@tanstack/react-router'
import AddIcon from '@mui/icons-material/Add'
import ReusableModal from '@components/Modal/ReusableModal.component'
import { useState, useEffect, useCallback, useMemo } from 'react'
import SearchIcon from 'icons/Search.icon'
import { useQuery, useMutation } from '@tanstack/react-query'
import axios from 'axios'

export const Route = createFileRoute(
  '/(dashboard)/_dashboard/customize-interview',
)({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      technologyId: search.technologyId as string,
    }
  },
  component: RouteComponent,
})

const StyledSearch = styled(TextField)(() => ({
  marginTop: '24px',
  input: {
    lineHeight: '20px',
  },

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--Interviewer-Lightest-Black)',
  },
}))

const StyledCard = styled(Card)(() => ({
  borderRadius: '12px',
  background: '#FFFFFF',
  border: '1px solid var(--Light-Gray)',
  padding: '20px',
  boxShadow: '0px 1px 2px 0px #0205111A',
  '&:hover': {
    boxShadow: '0px 1px 2px 0px #0205111A',
  },
  flexShrink: 0,
  marginRight: '10px',
}))

interface SearchCategory {
  id: string;
  name: string;
  description: string;
  totalNumberOfQuestions: number;
}

interface Category {
  id: string;
  name: string;
  description: string;
  recommendedNumberOfQuestions: number;
}

interface CategoryData {
  id: string;
  name: string;
  description: string;
  recommendedNumberOfQuestions: number;
  totalNumberOfQuestions: number;
}

function RouteComponent() {
  const [addCategory, setAddCategory] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [readTime, setReadTime] = useState(30)
  const [answerTime, setAnswerTime] = useState(90)
  const [currentStep, setCurrentStep] = useState<'categories' | 'timeSelection'>('categories')
  const [categories, setCategories] = useState<Category[]>([])
  
  const { technologyId } = useSearch({ from: '/(dashboard)/_dashboard/customize-interview' })
  const navigate = useNavigate()
  const location = useLocation()
  
  // Get categories from navigation state
  const passedCategories = (location.state as any)?.categories as CategoryData[] | undefined

  // Initialize categories from passed data when component mounts
  useEffect(() => {
    if (passedCategories && passedCategories.length > 0) {
      const formattedCategories = passedCategories.map(category => ({
        id: category.id,
        name: category.name,
        description: category.description,
        recommendedNumberOfQuestions: category.recommendedNumberOfQuestions
      }))
      setCategories(formattedCategories)
    }
  }, [passedCategories])

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // TanStack Query for search results
  const { 
    data: searchResults = [], 
    isLoading: isSearchLoading, 
    error: searchError,
    refetch: refetchSearch
  } = useQuery<SearchCategory[]>({
    queryKey: ['searchCategories', technologyId, debouncedSearchQuery],
    queryFn: async () => {
      if (!technologyId) {
        throw new Error('No technologyId found in URL')
      }

      const token = localStorage.getItem('token')
      const response = await axios.get(
        `https://interviewer.runasp.net/api/technologies/${technologyId}/categories/scale/search`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            searchTerm: debouncedSearchQuery
          }
        }
      )
      return response.data
    },
    enabled: !!technologyId && addCategory, // Only run when modal is open
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }, [])

  const handleAddCategory = useCallback((category: SearchCategory) => {
    setCategories(prev => {
      // Check if category is already added
      if (prev.some(cat => cat.id === category.id)) {
        return prev
      }
      
      // Add category with recommended number of questions
      return [...prev, {
        id: category.id,
        name: category.name,
        description: category.description,
        recommendedNumberOfQuestions: category.totalNumberOfQuestions
      }]
    })
  }, [])

  const handleRemoveCategory = useCallback((categoryId: string) => {
    setCategories(prev => prev.filter(cat => cat.id !== categoryId))
  }, [])

  const handleUpdateCategoryQuestions = useCallback((categoryId: string, numberOfQuestions: number) => {
    setCategories(prev => 
      prev.map(cat => 
        cat.id === categoryId 
          ? { ...cat, recommendedNumberOfQuestions: numberOfQuestions }
          : cat
      )
    )
  }, [])

  const handleNext = useCallback(() => {
    if (categories.length > 0) {
      setCurrentStep('timeSelection')
    }
  }, [categories.length])

  const handleBack = useCallback(() => {
    setCurrentStep('categories')
  }, [])

  // Memoize expensive computations
  const hasCategories = useMemo(() => categories.length > 0, [categories.length])
  
  const scaleCategories = useMemo(() => 
    categories.map(category => ({
      id: category.id,
      numberOfQuestions: category.recommendedNumberOfQuestions
    })), [categories]
  )

  // TanStack Query mutation for starting interview
  const startInterviewMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token')
      const response = await axios.post(
        'https://interviewer.runasp.net/api/submissionInterviews',
        {
          scaleParams: {
            readTime: readTime,
            answerTime: answerTime,
            scaleCategories: scaleCategories
          }
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )
      return response.data
    },
    onSuccess: (data) => {
      navigate({ 
        to: '/questions/$id', 
        params: { id: data.id },
        state: { readTime: readTime } as any
      })
    },
    onError: (error) => {
      console.error('Error starting interview:', error)
    }
  })

  const handleStartInterview = useCallback(() => {
    if (!hasCategories) {
      return
    }
    startInterviewMutation.mutate()
  }, [hasCategories, startInterviewMutation])

  return (
    <Box sx={{
      width: {xs: '100%', md: '80%'},
      mx: 'auto',
      padding: {xs: '20px 16px', md: '125px 75px'}
    }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          textAlign: 'center',
          pb: '32px',
        }}
      >
        <Typography variant="h1">
          {currentStep === 'categories' ? 'Customize your interview' : 'Interview duration'}
        </Typography>
        <Typography>
          {currentStep === 'categories' 
            ? 'Lorem ipsum, dolor sit amet consectetur adipisicing elit. Id, temporibus!'
            : 'Lorem ipsum, dolor sit amet consectetur adipisicing elit. Id, temporibus!'
          }
        </Typography>
      </Box>

      {currentStep === 'categories' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {
            categories.map((category) => (
              <CategoryCard
                key={category.id}
                id={category.id}
                title={category.name}
                description={category.description}
                recommendedQuestions={category.recommendedNumberOfQuestions}
                onRemove={() => handleRemoveCategory(category.id)}
                onQuestionsChange={handleUpdateCategoryQuestions}
              />
            ))
          }
        </Box>
      )}

      {currentStep === 'timeSelection' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '22px', mt: 4 }}>
          <TimeSelectionCard
            title="How much time do candidates have to read the question?"
            description="Adjust read time"
            value={readTime}
            onChange={setReadTime}
            max={300}
            min={0}
          />
          <TimeSelectionCard
            title="How much time do candidates have to answer the question?"
            description="Adjust answer time"
            value={answerTime}
            onChange={setAnswerTime}
            max={300}
            min={0}
          />
        </Box>
      )}

      {currentStep === 'categories' && (
        <Box
          sx={{
            mt: '24px',
            border: '2px dashed var(--Light-Gray)',
            borderRadius: '20px',
            padding: '40px',
            textAlign: 'center',
            backgroundColor: ""
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <IconButton
              onClick={() => setAddCategory(true)}
              sx={{
                borderRadius: '16px',
                boxShadow: '0px 1px 2px 0px #0205111A',
                width: '64px',
                height: '64px',
                mb: '16px',
                '&:hover': {
                  backgroundColor: 'var(--Light-Gray)',
                },
              }}
            >
              <AddIcon sx={{ fontSize: '32px', color: '#000000' }} />
            </IconButton>
            <Typography
              variant="h3"
              sx={{ color: 'var(--Interviewer-Lighter-Black)' }}
            >
              Add a category
            </Typography>
          </Box>
        </Box>
      )}

      {currentStep === 'categories' && (
        <Button
          fullWidth
          variant="contained"
          color="primary"
          sx={{ display: 'flex', mx: 'auto', mt: '32px' }}
          onClick={handleNext}
          disabled={!hasCategories}
        >
          Next
        </Button>
      )}

      {currentStep === 'timeSelection' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: '32px' }}>
          {startInterviewMutation.isError && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: 'error.main', mb: 2 }}>
                Failed to start interview. Please try again.
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={() => startInterviewMutation.mutate()}
              >
                Retry
              </Button>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              sx={{ flex: 1 }}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{ flex: 1 }}
              onClick={handleStartInterview}
              disabled={startInterviewMutation.isPending}
            >
              {startInterviewMutation.isPending ? 'Starting Interview...' : 'Start Interview'}
            </Button>
          </Box>
        </Box>
      )}

      <ReusableModal open={addCategory} onClose={() => setAddCategory(false)}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            textAlign: 'center',
          }}
        >
          <Typography variant="h1">Add a category</Typography>
          <Typography>
            Add a category from the list or search for a specific category
          </Typography>
        </Box>
        <StyledSearch
          fullWidth
          type="search"
          value={searchQuery}
          onChange={handleSearchChange}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          placeholder="Search for a category"
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            mt: '20px',
            width: '100%',
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          {isSearchLoading ? (
            <Typography sx={{ textAlign: 'center' }}>Loading...</Typography>
          ) : searchError ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: 'error.main', mb: 2 }}>
                Error loading categories. Please try again.
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={() => refetchSearch()}
              >
                Retry
              </Button>
            </Box>
          ) : searchResults.length > 0 ? (
            searchResults.map((category) => {
              const isAdded = categories.some(cat => cat.id === category.id)
              return (
                <StyledCard key={category.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h3" sx={{ pb: '8px' }}>
                        {category.name}
                      </Typography>
                      <Typography>
                        {category.description}
                      </Typography>
                    </Box>
                    <Box>
                      <Fab
                        sx={{
                          backgroundColor: isAdded ? 'gray' : 'black',
                          color: 'white',
                          '&:hover': { backgroundColor: isAdded ? 'gray' : '#333' },
                        }}
                        size="small"
                        aria-label="add"
                        onClick={() => handleAddCategory(category)}
                        disabled={isAdded}
                      >
                        <AddIcon />
                      </Fab>
                    </Box>
                  </Box>
                </StyledCard>
              )
            })
          ) : (
            <Typography sx={{ textAlign: 'center', color: 'gray' }}>
              No categories found
            </Typography>
          )}
        </Box>
      </ReusableModal>
    </Box>
  )
}
